import {
  HttpError,
  ROUTINE_TRIGGER_WEBHOOK,
  useStores,
  type WebhookEndpoint,
} from '@littlebird/sdk';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { WebhookEndpointFormModal } from '@/features/routines/webhook-endpoint-form-modal';
import { WebhookSecretRevealModal } from '@/features/routines/webhook-secret-reveal-modal';
import { WebhookSenderSecretModal } from '@/features/routines/webhook-sender-secret-modal';
import { fetchMyTeam } from '@/features/team/team.query';
import { logError } from '@/lib/errorLogging';

type ModalWrapper = <T>(show: () => Promise<T>) => Promise<T>;

const showDirectly: ModalWrapper = (show) => show();

const fetchEndpointTeam = async () => {
  const team = await fetchMyTeam();
  return team && { id: team.id, name: team.name };
};

/**
 * Creating, editing, re-keying, setting the sender's secret, and deleting webhook endpoints, each through its modal.
 *
 * `wrapModal` runs around every modal, so a caller that is itself a dialog can hide while one is up.
 */
export function useWebhookEndpointActions({
  context,
  wrapModal = showDirectly,
}: {
  context: string;
  wrapModal?: ModalWrapper;
}) {
  const { routines } = useStores();

  /** Resolves to the new endpoint's id, or null when the user backs out or creation fails. */
  const create = () =>
    wrapModal(async () => {
      const [team, presets] = await Promise.all([
        fetchEndpointTeam(),
        // The store logs a failure; the form then offers no presets
        routines.fetchWebhookPresets().catch(() => []),
      ]);
      const newEndpoint = await ModalsManager.showResolvable(
        WebhookEndpointFormModal,
        { team, presets }
      );
      if (!newEndpoint) return null;
      try {
        const created = await routines.createWebhookEndpoint(
          newEndpoint.name,
          newEndpoint.samplePayload ?? null,
          newEndpoint.teamId ?? null,
          newEndpoint.description
        );
        await ModalsManager.showResolvable(WebhookSecretRevealModal, {
          url: created.url,
          secret: created.secret,
        });
        return created.id;
      } catch (error) {
        if (error instanceof HttpError && error.status === 409) {
          toast.error(
            newEndpoint.teamId
              ? 'Your team already has a webhook endpoint with that name'
              : 'You already have a webhook endpoint with that name'
          );
        } else {
          toast.error('Failed to create the webhook endpoint');
          logError(error, 'Failed to create webhook endpoint', { context });
        }
        return null;
      }
    });

  const regenerateSecret = (endpointId: string) =>
    wrapModal(async () => {
      try {
        const credentials =
          await routines.rotateWebhookEndpointSecret(endpointId);
        await ModalsManager.showResolvable(
          WebhookSecretRevealModal,
          credentials
        );
      } catch (error) {
        toast.error('Failed to regenerate the webhook secret');
        logError(error, 'Failed to regenerate webhook secret', { context });
      }
    });

  const enterSenderSecret = (endpoint: WebhookEndpoint) =>
    wrapModal(async () => {
      const secret = await ModalsManager.showResolvable(
        WebhookSenderSecretModal,
        { endpointName: endpoint.name }
      );
      if (!secret) return;
      try {
        await routines.setWebhookEndpointSecret(endpoint.id, secret);
        toast.success(`Saved ${endpoint.name}'s secret`);
      } catch (error) {
        toast.error('Failed to save the webhook secret');
        logError(error, 'Failed to set webhook endpoint secret', { context });
      }
    });

  const edit = (endpoint: WebhookEndpoint) =>
    wrapModal(async () => {
      const changes = await ModalsManager.showResolvable(
        WebhookEndpointFormModal,
        { team: await fetchEndpointTeam(), endpoint }
      );
      if (!changes) return;
      try {
        await routines.editWebhookEndpoint(endpoint.id, changes);
      } catch (error) {
        if (error instanceof HttpError && error.userErrorMessage) {
          toast.error(error.userErrorMessage);
        } else {
          toast.error('Failed to save the webhook endpoint');
          logError(error, 'Failed to edit webhook endpoint', { context });
        }
      }
    });

  const remove = (endpoint: WebhookEndpoint) =>
    wrapModal(async () => {
      const affectedRoutines = routines.orderedData.filter((routine) =>
        routine.triggers.some(
          (trigger) =>
            trigger.kind === ROUTINE_TRIGGER_WEBHOOK &&
            trigger.webhookEndpointId === endpoint.id
        )
      ).length;
      const triggerNote =
        affectedRoutines === 0
          ? 'No routines use it.'
          : `${affectedRoutines} ${affectedRoutines === 1 ? 'routine loses its' : 'routines lose their'} webhook trigger.`;
      const teamNote = endpoint.teamId
        ? " Your teammates' routines that use it lose their webhook trigger too."
        : '';
      const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
        title: `Delete ${endpoint.name}?`,
        description: `Posts to its URL will stop working. ${triggerNote}${teamNote}`,
        confirmText: 'Delete',
        confirmVariant: 'destructive',
      });
      if (!confirmed) return;
      try {
        await routines.deleteWebhookEndpoint(endpoint.id);
      } catch (error) {
        toast.error('Failed to delete the webhook endpoint');
        logError(error, 'Failed to delete webhook endpoint', { context });
      }
    });

  return {
    create,
    regenerateSecret,
    enterSenderSecret,
    edit,
    remove,
  };
}
